import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-table-column-filter',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconButton,
    MatIcon,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule
  ],
  templateUrl: './table-column-filter.component.html',
  styleUrls: ['./table-column-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnFilterComponent implements OnInit {
  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;


  @HostListener('click', ['$event'])
  handleKeyDown(event: MouseEvent) {
    event.stopPropagation();
  }

  @Output() filterChange = new EventEmitter<FilterResponse | null>();

  type = input<TableColumnFilterType>('string');
  options = input<TableColumnFilterOption[]>([]);
  resetVersion = input<number>(0);

  form = new FormGroup({
    filter: new FormControl<FiltrationType | null>(FiltrationType.EQUALS),
    value: new FormControl<string | number | Date | null>(null),
    valueTo: new FormControl<string | number | Date | null>(null),
    values: new FormControl<Array<string | number> | null>(null),
  });

  filterOptions = computed(() => {
    switch (this.type()) {
      case 'date':
        return DATE_FILTER_OPTIONS;
      case 'number':
        return NUMBER_FILTER_OPTIONS;
      case 'multi-select':
        return MULTI_SELECT_FILTER_OPTIONS;
      default:
        return FILTER_OPTIONS;
    }
  });

  readonly FiltrationType = FiltrationType;

  isApplied = false;

  destroyRef = inject(DestroyRef);
  private lastResetVersion = 0;

  constructor() {
    effect(() => {
      const version = this.resetVersion();

      if (version === this.lastResetVersion) {
        return;
      }

      this.lastResetVersion = version;
      this.resetUiStateOnly();
    });
  }

  get filter() {
    return this.form.get('filter') as FormControl;
  }

  ngOnInit(): void {
    this.filter.setValue(this.getDefaultFilter());

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isApplied = false;
      });
  }

  apply() {
    this.filterChange.emit({
      ...this.form.value,
      type: this.type()
    } as FilterResponse);
    this.isApplied = true;
    this.trigger.closeMenu();
  }

  reset() {
    this.isApplied = false;
    this.form.setValue({
      filter: this.getDefaultFilter(),
      value: null,
      valueTo: null,
      values: null,
    });
    this.filterChange.emit(null);
    this.trigger.closeMenu();
  }

  cancel() {
    this.trigger.closeMenu();
  }

  get showStringInput(): boolean {
    return this.type() === 'string' && !this.isNoValueFilter();
  }

  get showDateInput(): boolean {
    return this.type() === 'date' && (
      this.filter.value === FiltrationType.ON
      || this.filter.value === FiltrationType.ON_OR_AFTER
      || this.filter.value === FiltrationType.ON_OR_BEFORE
    );
  }

  get showDateRangeInput(): boolean {
    return this.type() === 'date' && this.filter.value === FiltrationType.BETWEEN;
  }

  get showNumberInput(): boolean {
    return this.type() === 'number' && (
      this.filter.value === FiltrationType.EQUALS
      || this.filter.value === FiltrationType.DOES_NOT_EQUAL
      || this.filter.value === FiltrationType.GREATER_THAN
      || this.filter.value === FiltrationType.GREATER_OR_EQUAL
      || this.filter.value === FiltrationType.LESS_THAN
      || this.filter.value === FiltrationType.LESS_OR_EQUAL
    );
  }

  get showNumberRangeInput(): boolean {
    return this.type() === 'number' && this.filter.value === FiltrationType.BETWEEN;
  }

  get showMultiSelectInput(): boolean {
    return this.type() === 'multi-select' && !this.isNoValueFilter();
  }

  private isNoValueFilter(): boolean {
    return this.filter.value === FiltrationType.CONTAINS_DATA
      || this.filter.value === FiltrationType.DOES_NOT_CONTAIN_DATA;
  }

  private getDefaultFilter(): FiltrationType {
    return this.type() === 'date'
      ? FiltrationType.ON
      : FiltrationType.EQUALS;
  }

  private resetUiStateOnly(): void {
    this.isApplied = false;
    this.form.setValue({
      filter: this.getDefaultFilter(),
      value: null,
      valueTo: null,
      values: null,
    });
    this.trigger?.closeMenu();
  }
}

export interface FilterResponse<T = string> {
  filter: FiltrationType,
  value: T;
  valueTo?: T;
  values?: Array<string | number>;
  type?: TableColumnFilterType;
}

export interface TableColumnFilterOption {
  label: string;
  value: string | number;
}

export type TableColumnFilterType = 'string' | 'date' | 'number' | 'multi-select';

export enum FiltrationType {
  EQUALS,
  DOES_NOT_EQUAL,
  CONTAINS_DATA,
  DOES_NOT_CONTAIN_DATA,
  CONTAINS,
  DOES_NOT_CONTAIN,
  BEGINS_WITH,
  DOES_NOT_BEGIN_WITH,
  ENDS_WITH,
  DOES_NOT_END_WITH,

  ON,
  ON_OR_AFTER,
  ON_OR_BEFORE,
  TODAY,
  YESTERDAY,
  TOMORROW,
  THIS_WEEK,
  THIS_MONTH,
  THIS_YEAR,

  NEXT_MONTH,
  NEXT_YEAR,

  LAST_WEEK,
  LAST_7_DAYS,
  LAST_MONTH,
  LAST_YEAR,

  BETWEEN,
  GREATER_THAN,
  GREATER_OR_EQUAL,
  LESS_THAN,
  LESS_OR_EQUAL,
}

const FILTER_OPTIONS = [
  {
    title: 'Equals',
    type: FiltrationType.EQUALS,
  },
  {
    title: 'Does not equal',
    type: FiltrationType.DOES_NOT_EQUAL,
  },
  {
    title: 'Contains data',
    type: FiltrationType.CONTAINS_DATA,
  },
  {
    title: 'Does not contain data',
    type: FiltrationType.DOES_NOT_CONTAIN_DATA,
  },
  {
    title: 'Contains',
    type: FiltrationType.CONTAINS,
  },
  {
    title: 'Does not contain',
    type: FiltrationType.DOES_NOT_CONTAIN,
  },
  {
    title: 'Begins with',
    type: FiltrationType.BEGINS_WITH,
  },
  {
    title: 'Does not begin with',
    type: FiltrationType.DOES_NOT_BEGIN_WITH,
  },
  {
    title: 'Ends with',
    type: FiltrationType.ENDS_WITH,
  },
  {
    title: 'Does not end with',
    type: FiltrationType.DOES_NOT_END_WITH,
  },
];


const DATE_FILTER_OPTIONS = [
  {
    title: 'On',
    type: FiltrationType.ON,
  },
  {
    title: 'On or after',
    type: FiltrationType.ON_OR_AFTER,
  },
  {
    title: 'On or before',
    type: FiltrationType.ON_OR_BEFORE,
  },
  {
    title: 'Between',
    type: FiltrationType.BETWEEN,
  },
  {
    title: 'Today',
    type: FiltrationType.TODAY,
  },
  {
    title: 'Yesterday',
    type: FiltrationType.YESTERDAY,
  },
  {
    title: 'Tomorrow',
    type: FiltrationType.TOMORROW,
  },
  {
    title: 'This week',
    type: FiltrationType.THIS_WEEK,
  },
  {
    title: 'This month',
    type: FiltrationType.THIS_MONTH,
  },
  {
    title: 'This year',
    type: FiltrationType.THIS_YEAR,
  },
  {
    title: 'Next month',
    type: FiltrationType.NEXT_MONTH,
  },
  {
    title: 'Next year',
    type: FiltrationType.NEXT_YEAR,
  },
  {
    title: 'Last week',
    type: FiltrationType.LAST_WEEK,
  },
  {
    title: 'Last 7 days',
    type: FiltrationType.LAST_7_DAYS,
  },
  {
    title: 'Last month',
    type: FiltrationType.LAST_MONTH,
  },
  {
    title: 'Last year',
    type: FiltrationType.LAST_YEAR,
  },
];

const NUMBER_FILTER_OPTIONS = [
  {
    title: 'Equals',
    type: FiltrationType.EQUALS,
  },
  {
    title: 'Does not equal',
    type: FiltrationType.DOES_NOT_EQUAL,
  },
  {
    title: 'Greater than',
    type: FiltrationType.GREATER_THAN,
  },
  {
    title: 'Greater than or equal',
    type: FiltrationType.GREATER_OR_EQUAL,
  },
  {
    title: 'Less than',
    type: FiltrationType.LESS_THAN,
  },
  {
    title: 'Less than or equal',
    type: FiltrationType.LESS_OR_EQUAL,
  },
  {
    title: 'Between',
    type: FiltrationType.BETWEEN,
  },
  {
    title: 'Contains data',
    type: FiltrationType.CONTAINS_DATA,
  },
  {
    title: 'Does not contain data',
    type: FiltrationType.DOES_NOT_CONTAIN_DATA,
  },
];

const MULTI_SELECT_FILTER_OPTIONS = [
  {
    title: 'Equals',
    type: FiltrationType.EQUALS,
  },
  {
    title: 'Does not equal',
    type: FiltrationType.DOES_NOT_EQUAL,
  },
  {
    title: 'Contains data',
    type: FiltrationType.CONTAINS_DATA,
  },
  {
    title: 'Does not contain data',
    type: FiltrationType.DOES_NOT_CONTAIN_DATA,
  },
];
